import { getCurrentUserIri } from '~/api/utils'
import type {
  CallScheduleEvent,
  MeetingApplicantEvent,
  NotesEvent,
  SendEmailEvent,
  SendSmsEvent,
  StatusUpdateEvent,
  TaskEvent
} from '~/domains/actions/types'
import type { OrderHead } from '~/domains/orders/types'
import type { TaxonomyItem } from '~/domains/taxonomies/types'
import { toISOLocal } from '~/utils/date'

// Generate a temporary negative ID for optimistic updates
let tempId = -1
const getTempId = () => tempId--

const getNow = () => {
  const now = new Date(Date.now() - 1000) // 1 second ago to avoid race conditions (can show future date in optimistic updates)
  return toISOLocal(now)
}

export const generateCallEvent = (
  applicantIds: number[],
  payload: {
    datetime?: Date
    notes?: string
    isCompleted?: boolean
    order?: OrderHead
    actionType?: string
  }
): CallScheduleEvent => {
  return {
    id: getTempId(),
    type: 'recall',
    created: getNow(),
    scheduledDate: payload.datetime ? toISOLocal(payload.datetime) : undefined,
    notes: payload.notes,
    isCompleted: payload.isCompleted,
    applicants: applicantIds.map((id) => `/v1/applicants/${id}`),
    order: payload.order,
    createdBy: getCurrentUserIri(),
    actionType: payload.actionType ?? undefined
  }
}

export const generateNoteEvent = (
  applicantIds: number[],
  payload: {
    notes: string
    order?: OrderHead
    actionType?: string
  }
): NotesEvent => {
  return {
    id: getTempId(),
    type: 'notes',
    created: getNow(),
    notes: payload.notes,
    actionType: payload.actionType,
    applicants: applicantIds.map((id) => `/v1/applicants/${id}`),
    order: payload.order,
    createdBy: getCurrentUserIri()
  }
}

export const generateMessageEvent = (
  applicantIds: number[],
  payload: {
    message: string
    order?: OrderHead
  }
): SendSmsEvent => {
  return {
    id: getTempId(),
    type: 'sms_sent',
    created: getNow(),
    notes: payload.message
      ? payload.message.replace(/(<([^>]+)>)/gi, '').substring(0, 300)
      : undefined,
    applicants: applicantIds.map((id) => `/v1/applicants/${id}`),
    order: payload.order,
    createdBy: getCurrentUserIri()
  }
}

export const generateEmailEvent = (
  applicantIds: number[],
  payload: {
    subject?: string
    content: string
    order?: OrderHead
  }
): SendEmailEvent => {
  return {
    id: getTempId(),
    type: 'send_email',
    created: getNow(),
    subject: payload.subject,
    excerpt: payload.content
      ? payload.content.replace(/(<([^>]+)>)/gi, '').substring(0, 300)
      : undefined,
    applicants: applicantIds.map((id) => `/v1/applicants/${id}`),
    order: payload.order,
    createdBy: getCurrentUserIri()
  }
}

export const generateMeetingEvent = (
  applicantIds: number[],
  payload: {
    fromDatetime: Date
    toDatetime: Date
    subject?: string
    notes?: string
    contacts: number[]
    order?: OrderHead
  }
): MeetingApplicantEvent => {
  return {
    id: getTempId(),
    type: 'meeting_applicant',
    created: getNow(),
    scheduledDate: toISOLocal(payload.fromDatetime),
    notes: payload.subject
      ? `${payload.subject}${payload.notes ? `\n\n${payload.notes}` : ''}`
      : payload.notes,
    applicants: applicantIds.map((id) => `/v1/applicants/${id}`),
    contacts: payload.contacts.map((id) => `/v1/contacts/${id}`),
    order: payload.order,
    createdBy: getCurrentUserIri()
  }
}

export const generateStatusUpdateEvent = (
  applicantIds: number[],
  payload: {
    status?: TaxonomyItem<'statuses'>
    orderStatus?: TaxonomyItem<'applicant_order_statuses'>
    order?: OrderHead
    contacts?: number[]
    archiveType?: TaxonomyItem<'archive_types'>
    notes?: string
  }
): StatusUpdateEvent => {
  return {
    id: getTempId(),
    type: 'status_update',
    created: getNow(),
    status: payload.status,
    orderStatus: payload.orderStatus,
    order: payload.order,
    applicants: applicantIds.map((id) => `/v1/applicants/${id}`),
    contacts: payload.contacts?.map((id) => `/v1/contacts/${id}`),
    archiveType: payload.archiveType,
    notes: payload.notes,
    createdBy: getCurrentUserIri()
  }
}

export const generateTaskEvent = (
  applicantIds: number[],
  payload: {
    subject: string
    scheduledDate?: Date | null
    notes?: string
    isCompleted?: boolean
    actionType?: string
    assignedUser?: string
  }
): TaskEvent => {
  return {
    id: getTempId(),
    type: 'task',
    created: getNow(),
    scheduledDate: payload.scheduledDate
      ? toISOLocal(payload.scheduledDate)
      : undefined,
    subject: payload.subject,
    notes: payload.notes,
    isCompleted: payload.isCompleted,
    actionType: payload.actionType,
    applicants: applicantIds.map((id) => `/v1/applicants/${id}`),
    createdBy: getCurrentUserIri(),
    assignedUser: payload.assignedUser
  }
}
